import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useRef, useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import type { ProcessStep } from "@/lib/content";

const easeOut = [0.16, 1, 0.3, 1] as const;

const STEP_SCROLL_HEIGHT = 220;
const SVG_WIDTH = 80;
const FIXED_TOP_VH = 33;
const REVEAL_TRIGGER = 0.55;

function buildSCurvePath(count: number, stepH: number): string {
  const parts: string[] = [];
  const circleR = 28;
  for (let i = 0; i < count - 1; i++) {
    const y0 = i * stepH + circleR + circleR + 4;
    const y1 = (i + 1) * stepH + circleR - circleR - 4;
    const midY = (y0 + y1) / 2;
    const amp = 20;
    const dir = i % 2 === 0 ? 1 : -1;
    const cx = 40 + amp * dir;
    const cx2 = 40 - amp * dir;
    if (i === 0) parts.push(`M 40 ${y0}`);
    parts.push(`C ${cx} ${y0 + (midY - y0) * 0.4}, ${cx} ${midY - 15}, 40 ${midY}`);
    parts.push(`C ${cx2} ${midY + 15}, ${cx2} ${y1 - (y1 - midY) * 0.4}, 40 ${y1}`);
  }
  return parts.join(" ");
}

function StepItem({
  step,
  isRevealed,
  stepRef,
}: {
  step: ProcessStep;
  isRevealed: boolean;
  stepRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={stepRef}
      data-step={step.number}
      className="flex items-start gap-5 sm:gap-6 lg:gap-8"
      style={{ minHeight: STEP_SCROLL_HEIGHT, paddingBottom: 40 }}
    >
      <div className="relative flex-shrink-0" style={{ zIndex: 5 }}>
        <div
          style={{
            position: "absolute",
            inset: -8,
            borderRadius: "50%",
            background: "#0a0a0a",
            zIndex: 1,
          }}
        />
        <div
          className="absolute pointer-events-none transition-all duration-700"
          style={{
            inset: -6,
            borderRadius: "50%",
            border: isRevealed ? "1px solid rgba(6,182,212,0.25)" : "1px solid transparent",
            opacity: isRevealed ? 1 : 0,
            zIndex: 3,
          }}
        />
        <div
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-[0.95rem] sm:text-[1.05rem] font-semibold transition-all duration-700"
          style={{
            position: "relative",
            zIndex: 2,
            ...(isRevealed
              ? {
                  background: "linear-gradient(160deg, #22d3ee, #06b6d4, #0e7490, #164e63)",
                  border: "1px solid rgba(6,182,212,0.3)",
                  color: "#fff",
                  boxShadow: "0 0 24px rgba(6,182,212,0.3), 0 0 48px rgba(6,182,212,0.1)",
                }
              : {
                  background: "#0a0a0a",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.55)",
                  boxShadow: "none",
                }),
          }}
        >
          {step.number}
        </div>
      </div>
      <div className="pt-1 sm:pt-2">
        <h3
          className="text-lg sm:text-xl lg:text-[1.35rem] font-semibold mb-2 leading-snug transition-colors duration-700"
          style={{
            color: isRevealed ? "#ffffff" : "rgba(255,255,255,0.55)",
            textShadow: "0 2px 12px rgba(0,0,0,0.5)",
          }}
        >
          {step.title}
        </h3>
        <p
          className="text-[0.9rem] sm:text-[0.95rem] leading-relaxed max-w-[420px] transition-colors duration-700"
          style={{
            color: isRevealed ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.45)",
            textShadow: "0 2px 10px rgba(0,0,0,0.6)",
          }}
        >
          {step.desc}
        </p>
      </div>
    </div>
  );
}

function DashedLine({
  svgPath,
  totalHeight,
  clipHeightPx,
}: {
  svgPath: string;
  totalHeight: number;
  clipHeightPx: number;
}) {
  const clipId = "process-line-clip";
  return (
    <svg
      width={SVG_WIDTH}
      height={totalHeight}
      viewBox={`0 0 ${SVG_WIDTH} ${totalHeight}`}
      fill="none"
      style={{ overflow: "visible" }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={SVG_WIDTH} height={clipHeightPx} />
        </clipPath>
      </defs>
      <path
        d={svgPath}
        stroke="rgba(103, 232, 249, 0.75)"
        strokeWidth="2"
        strokeDasharray="2 8"
        fill="none"
        strokeLinecap="round"
        clipPath={`url(#${clipId})`}
      />
    </svg>
  );
}

function CtaArrow() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-all duration-300 group-hover/cta:translate-x-1 group-hover/cta:stroke-white"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

export const ProcessSection = () => {
  const { t } = useLanguage();
  const steps = t.process.steps;
  const rightRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [revealedSteps, setRevealedSteps] = useState(0);
  const [lineClipHeight, setLineClipHeight] = useState(0);
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    let rafId: number;
    const onScroll = () => {
      rafId = requestAnimationFrame(() => {
        if (typeof window === "undefined") return;
        const triggerY = window.innerHeight * REVEAL_TRIGGER;
        const rightEl = rightRef.current;
        if (!rightEl) return;
        const rightRect = rightEl.getBoundingClientRect();

        let count = 0;
        for (let i = 0; i < stepRefs.current.length; i++) {
          const el = stepRefs.current[i];
          if (!el) continue;
          if (el.getBoundingClientRect().top + 28 <= triggerY) count = i + 1;
        }
        setRevealedSteps(count);

        if (count > 0 && count < steps.length) {
          const lastEl = stepRefs.current[count - 1];
          const nextEl = stepRefs.current[count];
          if (lastEl && nextEl) {
            const lastCenterY = lastEl.getBoundingClientRect().top - rightRect.top + 28;
            const nextCenterY = nextEl.getBoundingClientRect().top - rightRect.top + 28;
            const lastCV = lastEl.getBoundingClientRect().top + 28;
            const nextCV = nextEl.getBoundingClientRect().top + 28;
            const frac =
              nextCV - lastCV > 0
                ? Math.max(0, Math.min(1, (triggerY - lastCV) / (nextCV - lastCV)))
                : 0;
            setLineClipHeight(lastCenterY + (nextCenterY - lastCenterY) * frac);
          }
        } else if (count >= steps.length) {
          const lastEl = stepRefs.current[steps.length - 1];
          if (lastEl && rightRef.current) {
            setLineClipHeight(
              lastEl.getBoundingClientRect().top - rightRef.current.getBoundingClientRect().top + 28
            );
          }
        } else {
          setLineClipHeight(0);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [steps.length]);

  const setStepRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      stepRefs.current[index] = el;
    },
    []
  );

  const totalSvgHeight = steps.length * STEP_SCROLL_HEIGHT;
  const svgPath = buildSCurvePath(steps.length, STEP_SCROLL_HEIGHT);

  return (
    <section
    className="relative bg-transparent pt-16 md:pt-40 lg:pt-16 mb-[6vh] lg:mb-[12vh]"
    style={{ zIndex: 10 }}
  >
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        {/* Section heading with scroll anchor */}
        <div className="relative">
          <div id="spoluprace" className="absolute -top-4 lg:-top-8 scroll-mt-16 lg:scroll-mt-20" />
          <h2
            className="text-4xl sm:text-5xl lg:pt-8 lg:text-6xl font-bold mb-20"
            style={{
              color: "#ffffff",
              textShadow: "0 2px 12px rgba(0, 0, 0, 0.5)",
            }}
          >
            {t.process.title}
          </h2>
        </div>

        {/* MOBILE: subtitle + CTA */}
        {!isDesktop && (
          <div style={{ marginBottom: 48 }}>
            <p
              style={{
                fontSize: "0.95rem",
                lineHeight: 1.7,
                marginBottom: 32,
                maxWidth: 500,
                    color: "rgba(255,255,255,0.85)",
              }}
            >
              {t.process.subtitle}
            </p>
            <Button
              asChild
              variant="heroPrimary"
              size="xl"
              className="text-white border border-white hover:[background:white!important] hover:text-black transition-all duration-300 group font-bold text-base"
              style={{ background: "linear-gradient(135deg, #000000 50%, #00d4ff40 100%)" }}
            >
              <a href="#kontakt">
                {t.process.cta}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-all duration-300 group-hover:translate-x-1">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </Button>
          </div>
        )}

        {/* Main grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isDesktop ? "420px 1fr" : "1fr",
            gap: isDesktop ? 80 : 0,
            alignItems: "start",
            marginTop: isDesktop ? 48 : 0,
          }}
        >
          {/* LEFT: desktop only */}
          {isDesktop && (
            <div style={{ position: "sticky", top: `${FIXED_TOP_VH}vh` }}>
              <div style={{ paddingRight: 32 }}>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: easeOut, delay: 0.1 }}
                  style={{
                    fontSize: "1rem",
                    lineHeight: 1.7,
                    marginBottom: 40,
                    maxWidth: 400,
                    color: "rgba(255,255,255,0.85)",
                    textShadow: "0 2px 10px rgba(0,0,0,0.6)",
                  }}
                >
                  {t.process.subtitle}
                </motion.p>
                <Button
                  asChild
                  variant="heroPrimary"
                  size="xl"
                  className="text-white border border-white hover:[background:white!important] hover:text-black transition-all duration-300 group font-bold text-base"
                  style={{ background: "linear-gradient(135deg, #000000 50%, #00d4ff40 100%)" }}
                >
                  <a href="#kontakt">
                    {t.process.cta}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-all duration-300 group-hover:translate-x-1">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </a>
                </Button>
              </div>
            </div>
          )}

          {/* RIGHT: steps + line */}
          <div ref={rightRef} className="relative" style={{ paddingLeft: 8 }}>
            <div
              className="absolute pointer-events-none"
              style={{
                left: -12,
                top: 0,
                width: SVG_WIDTH,
                height: totalSvgHeight,
                zIndex: 2,
              }}
            >
              <DashedLine
                svgPath={svgPath}
                totalHeight={totalSvgHeight}
                clipHeightPx={lineClipHeight}
              />
            </div>
            {steps.map((step, i) => (
              <StepItem
                key={step.number}
                step={step}
                isRevealed={i < revealedSteps}
                stepRef={setStepRef(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
